import { type Currency } from '@principle-theorem/accounting';
import {
  type DocumentReference,
  type ISoftDelete,
  type Timestamp,
  TypeGuard,
  isDocRef,
  isObject,
  isTimestamp,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import type { IAppointment } from '../appointment/appointment';
import type { IAppointmentRequest } from '../appointment/appointment-request';
import type { ITreatmentTemplate } from '../clinical-charting/treatment/treatment-template';
import type { IPractice } from '../practice/practice';
import { DepositCaptureStrategy } from './deposit-capture-strategy';
import { DepositPaymentMode } from './deposit-payment-mode';
import type { IDepositRequest } from './deposit-request';

export enum DepositPaymentAttemptProvider {
  Stripe = 'stripe',
}

export enum DepositPaymentAttemptChannel {
  OnlineBooking = 'onlineBooking',
  PaymentLink = 'paymentLink',
  Terminal = 'terminal',
  Moto = 'moto',
  Manual = 'manual',
}

export enum DepositPaymentAttemptStatus {
  Created = 'created',
  AwaitingCustomer = 'awaitingCustomer',
  Processing = 'processing',
  Authorised = 'authorised',
  Captured = 'captured',
  Failed = 'failed',
  Cancelled = 'cancelled',
  Expired = 'expired',
  Refunded = 'refunded',
}

export interface IDepositPaymentAttempt extends ISoftDelete {
  readonly practiceRef: DocumentReference<IPractice>;
  readonly depositRequestRef?: DocumentReference<IDepositRequest>;
  readonly appointmentRef?: DocumentReference<IAppointment>;
  readonly appointmentRequestRef?: DocumentReference<IAppointmentRequest>;
  readonly treatmentTemplateRef?: DocumentReference<ITreatmentTemplate>;
  readonly appointmentStart?: Timestamp;
  readonly provider: DepositPaymentAttemptProvider;
  readonly channel: DepositPaymentAttemptChannel;
  readonly providerReferenceId?: string;
  /**
   * The connected account the charge was created on (`acct_…`). Attempts
   * taken on Principle's own account have none, and every action on an
   * attempt fails closed without it.
   */
  readonly providerAccountId?: string;
  readonly amountCents: number;
  readonly currency: Currency;
  readonly paymentMode: DepositPaymentMode;
  readonly captureStrategy?: DepositCaptureStrategy;
  readonly status: DepositPaymentAttemptStatus;
  readonly cancellationReason?: string;
  readonly lastError?: string;
  readonly authorisedAt?: Timestamp;
  readonly capturedAt?: Timestamp;
  readonly failedAt?: Timestamp;
  readonly refundedAt?: Timestamp;
  /**
   * The Stripe Refund id (`re_…`). Sibling to `providerReferenceId`, which
   * holds the PaymentIntent. Without it a refunded deposit cannot be tied back
   * to the money that actually moved at the provider.
   */
  readonly refundProviderReferenceId?: string;
}

export const isDepositPaymentAttempt =
  TypeGuard.interface<IDepositPaymentAttempt>({
    practiceRef: isDocRef,
    depositRequestRef: TypeGuard.nilOr(isDocRef),
    appointmentRef: TypeGuard.nilOr(isDocRef),
    appointmentRequestRef: TypeGuard.nilOr(isDocRef),
    treatmentTemplateRef: TypeGuard.nilOr(isDocRef),
    appointmentStart: TypeGuard.nilOr(isTimestamp),
    provider: TypeGuard.enumValue(DepositPaymentAttemptProvider),
    channel: TypeGuard.enumValue(DepositPaymentAttemptChannel),
    providerReferenceId: TypeGuard.nilOr(isString),
    providerAccountId: TypeGuard.nilOr(isString),
    amountCents: isNumber,
    currency: isString,
    paymentMode: TypeGuard.enumValue(DepositPaymentMode),
    captureStrategy: TypeGuard.nilOr(
      TypeGuard.enumValue(DepositCaptureStrategy)
    ),
    status: TypeGuard.enumValue(DepositPaymentAttemptStatus),
    cancellationReason: TypeGuard.nilOr(isString),
    lastError: TypeGuard.nilOr(isString),
    authorisedAt: TypeGuard.nilOr(isObject),
    capturedAt: TypeGuard.nilOr(isObject),
    failedAt: TypeGuard.nilOr(isObject),
    refundedAt: TypeGuard.nilOr(isObject),
    refundProviderReferenceId: TypeGuard.nilOr(isString),
    deleted: isBoolean,
  });
